<template>
  <div class="min-h-screen bg-[#F7F9FC]">
    <ResponsiveView desktop-only>
      <div class="min-h-screen bg-[#F7F9FC] flex flex-col lg:flex-row">
        <SidebarComponent class="hidden lg:block" />

        <div class="flex-1 flex flex-col">
          <HeaderComponent class="hidden lg:flex" />

          <main class="flex-1 px-4 lg:px-10 py-6 overflow-y-auto">
            <div class="max-w-6xl mx-auto space-y-8">
              <button
                class="flex items-center text-[#1E1E1F] text-sm font-semibold gap-2 hover:text-blue-600 transition"
                type="button"
                @click="router.back()"
              >
                <i class="fas fa-arrow-left text-base" />
                {{ t("tutorBuilder.back") }}
              </button>

              <!-- Step1Card -->
              <section
                class="bg-white rounded-[32px] border border-gray-100 shadow-sm p-6 lg:p-8 max-w-3xl mx-auto"
              >
                <p class="text-xs font-semibold uppercase tracking-[0.2em] text-gray-400">
                  {{ t("tutorBuilder.step1.label") }}
                </p>
                <h2 class="text-2xl font-semibold text-[#1E1E1F] mt-1">
                  {{ t("tutorBuilder.step1.title") }}
                </h2>
                <p class="text-sm text-gray-500 mt-2">
                  {{ t("tutorBuilder.step1.description") }}
                </p>

                <div
                  v-if="!userIdAvailable"
                  class="rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-800 mt-4"
                >
                  {{ t("tutorBuilder.step1.loginRequired") }}
                </div>

                <div
                  v-if="dropdownLoading"
                  class="rounded-2xl border border-dashed border-blue-200 bg-blue-50 px-4 py-3 text-xs text-blue-700 mt-4"
                >
                  {{ t("tutorBuilder.step1.loading") }}
                </div>

                <form
                  class="space-y-5 mt-6"
                  @submit.prevent="submitQuizShell"
                >
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">
                      {{ t("tutorBuilder.step1.fields.titleLabel") }}
                    </label>
                    <textarea
                      v-model.trim="quizForm.title"
                      rows="4"
                      :placeholder="t('tutorBuilder.step1.fields.titlePlaceholder')"
                      class="w-full rounded-2xl border border-gray-200 px-4 py-3 text-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    />
                  </div>

                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label class="block text-sm font-medium text-gray-700 mb-2">
                        {{ t("tutorBuilder.step1.fields.subjectLabel") }}
                      </label>
                      <select
                        v-model="quizForm.subjectId"
                        class="w-full rounded-2xl border border-gray-200 px-4 py-3 text-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                        :disabled="subjectOptions.length === 0 || dropdownLoading"
                      >
                        <option
                          disabled
                          value=""
                        >
                          {{ t("tutorBuilder.step1.fields.subjectPlaceholder") }}
                        </option>
                        <option
                          v-for="subject in subjectOptions"
                          :key="subject.id"
                          :value="subject.id"
                        >
                          {{ subject.name }}
                        </option>
                      </select>
                    </div>

                    <div>
                      <label class="block text-sm font-medium text-gray-700 mb-2">
                        {{ t("tutorBuilder.step1.fields.gradeLabel") }}
                      </label>
                      <select
                        v-model="quizForm.gradeId"
                        class="w-full rounded-2xl border border-gray-200 px-4 py-3 text-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                        :disabled="gradeOptions.length === 0 || dropdownLoading"
                      >
                        <option
                          disabled
                          value=""
                        >
                          {{ t("tutorBuilder.step1.fields.gradePlaceholder") }}
                        </option>
                        <option
                          v-for="grade in gradeOptions"
                          :key="grade.id"
                          :value="grade.id"
                        >
                          {{ grade.name }}
                        </option>
                      </select>
                    </div>
                  </div>

                  <div class="flex items-center justify-end gap-4 pt-2">
                    <button
                      type="button"
                      class="px-6 py-3 rounded-2xl text-sm font-semibold border border-gray-200 text-gray-700 hover:bg-gray-50 transition"
                      @click="resetBuilder"
                    >
                      {{ t("tutorBuilder.step1.buttons.reset") }}
                    </button>
                    <button
                      type="submit"
                      class="px-8 py-3 rounded-2xl text-sm font-semibold text-white bg-blue-600 hover:bg-blue-500 transition disabled:opacity-60 disabled:cursor-not-allowed"
                      :disabled="isCreatingQuiz || !userIdAvailable"
                    >
                      <span v-if="isCreatingQuiz">{{ t("tutorBuilder.step1.buttons.submit") }}...</span>
                      <span v-else>{{ t("tutorBuilder.step1.buttons.submit") }}</span>
                    </button>
                  </div>

                  <div
                    v-if="createQuizMessage || createdQuizGuid"
                    class="rounded-2xl border border-blue-100 bg-blue-50 px-4 py-3 text-xs text-blue-700"
                  >
                    <p v-if="createdQuizGuid">
                      {{ t("tutorBuilder.step1.activeGuid") }}
                      <span class="font-semibold">{{ createdQuizGuid }}</span>
                    </p>
                    <p
                      v-if="createQuizMessage"
                      class="mt-1"
                    >
                      {{ createQuizMessage }}
                    </p>
                  </div>
                </form>
              </section>

              <!-- Step2Card -->
              <section
                class="relative bg-white rounded-[32px] border border-gray-100 shadow-sm p-6 lg:p-8"
              >
                <div
                  v-if="questionSectionLocked"
                  class="absolute inset-0 bg-white/80 backdrop-blur-sm rounded-[32px] border border-dashed border-gray-200 grid place-items-center text-center px-6 text-sm text-gray-500 z-10"
                >
                  {{ t("tutorBuilder.step2.locked") }}
                </div>

                <div :class="{ 'opacity-40 pointer-events-none select-none': questionSectionLocked }">
                  <header class="mb-6">
                    <p class="text-xs font-semibold uppercase tracking-[0.3em] text-gray-400">
                      {{ t("tutorBuilder.step2.label") }}
                    </p>
                    <h2 class="text-3xl font-semibold text-[#1E1E1F] mt-1">
                      {{ t("tutorBuilder.step2.title") }}
                    </h2>
                    <p class="text-sm text-gray-500 mt-2">
                      {{ t("tutorBuilder.step2.description") }}
                    </p>
                  </header>

                  <form
                    class="space-y-6"
                    @submit.prevent="addQuestion"
                  >
                    <div>
                      <label class="text-sm font-medium text-gray-700 mb-2 block">
                        {{ t("tutorBuilder.step2.questionLabel") }}
                      </label>
                      <textarea
                        v-model.trim="questionForm.question"
                        rows="3"
                        :placeholder="t('tutorBuilder.step2.questionPlaceholder')"
                        class="w-full rounded-2xl border border-gray-200 px-4 py-3 text-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                      />
                    </div>

                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div
                        v-for="choice in questionChoicesPrimary"
                        :key="choice"
                        class="flex flex-col"
                      >
                        <label class="text-sm font-semibold text-gray-700 mb-2">
                          {{ choice }} :
                        </label>
                        <input
                          v-model.trim="questionForm[`choice${choice}`]"
                          type="text"
                          :placeholder="t('tutorBuilder.step2.choicePlaceholder')"
                          class="rounded-2xl border border-gray-200 px-4 py-3 text-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                      </div>
                    </div>

                    <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div
                        v-for="choice in questionChoicesExtra"
                        :key="choice"
                        class="flex flex-col"
                      >
                        <label class="text-sm font-semibold text-gray-700 mb-2">
                          {{ choice }} :
                        </label>
                        <input
                          v-model.trim="questionForm[`choice${choice}`]"
                          type="text"
                          :placeholder="t('tutorBuilder.step2.choiceOptionalPlaceholder')"
                          class="rounded-2xl border border-gray-200 px-4 py-3 text-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                      </div>
                    </div>

                    <div>
                      <p class="text-sm font-semibold text-gray-700 mb-2">
                        {{ t("tutorBuilder.step2.dropFileTitle") }}
                      </p>
                      <div
                        class="rounded-3xl border-2 border-dashed border-gray-200 px-6 py-10 text-center flex flex-col items-center gap-4 bg-gray-50/50"
                      >
                        <i class="fas fa-cloud-upload-alt text-3xl text-blue-500" />
                        <p class="text-sm text-gray-500">
                          {{ t("tutorBuilder.step2.dropFileInstruction") }}
                        </p>
                        <p class="text-xs text-gray-400">
                          {{ t("tutorBuilder.step2.dropFileHint") }}
                        </p>
                        <div class="flex items-center gap-3 flex-wrap justify-center">
                          <button
                            type="button"
                            class="px-4 py-2 rounded-full border border-gray-200 text-sm text-gray-700 bg-white"
                            @click="browseAttachment"
                          >
                            {{ t("tutorBuilder.step2.browseFile") }}
                          </button>
                          <span class="text-xs text-gray-500">
                            {{ attachmentLabel }}
                          </span>
                          <button
                            v-if="questionForm.attachment"
                            type="button"
                            class="text-xs text-red-500 underline"
                            @click="removeAttachment"
                          >
                            {{ t("tutorBuilder.step2.removeFile") }}
                          </button>
                        </div>
                        <input
                          ref="attachmentInput"
                          type="file"
                          class="hidden"
                          accept=".pdf,image/*"
                          @change="onAttachmentChange"
                        >
                      </div>
                    </div>

                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p class="text-sm font-semibold text-gray-700 mb-3">
                          {{ t("tutorBuilder.step2.chooseAnswer") }}
                        </p>
                        <div class="flex flex-wrap gap-3">
                          <button
                            v-for="choice in questionChoicesPrimary"
                            :key="`answer-${choice}`"
                            type="button"
                            class="w-12 h-12 rounded-2xl border font-semibold text-sm transition"
                            :class="choice === questionForm.correctChoice
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'border-gray-200 text-gray-600 bg-white'"
                            @click="questionForm.correctChoice = choice"
                          >
                            {{ choice }}
                          </button>
                        </div>
                      </div>
                      <div class="flex items-center gap-3">
                        <input
                          id="multiple-answer"
                          v-model="questionForm.isMultipleAnswer"
                          type="checkbox"
                          class="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        >
                        <label
                          for="multiple-answer"
                          class="text-sm text-gray-600"
                        >
                          {{ t("tutorBuilder.step2.allowMultiple") }}
                        </label>
                      </div>
                    </div>

                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
                      <button
                        type="button"
                        class="px-6 py-3 rounded-2xl border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition"
                        @click="scrollToQuestionList"
                      >
                        {{ t("tutorBuilder.step2.backToList") }}
                      </button>
                      <!-- <button
                        type="button"
                        class="px-6 py-3 rounded-2xl border border-blue-200 text-sm font-semibold text-blue-600 hover:bg-blue-50 transition"
                        @click="resetQuestionForm"
                      >
                        New Question
                      </button> -->
                      <button
                        type="submit"
                        class="px-6 py-3 rounded-2xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-500 transition disabled:opacity-60 disabled:cursor-not-allowed"
                        :disabled="isSavingQuestion || questionSectionLocked"
                      >
                        <span v-if="isSavingQuestion">{{ t("tutorBuilder.step1.buttons.submit") }}...</span>
                        <span v-else>{{ t("tutorBuilder.step2.newQuestion") }}</span>
                      </button>
                    </div>

                    <p class="text-center text-xs text-gray-400">
                      {{ t("tutorBuilder.step2.helper") }}
                    </p>
                  </form>
                </div>
              </section>
              <!-- Step3Card -->
              <section
                ref="questionListSection"
                class="bg-white rounded-[32px] border border-gray-100 shadow-sm p-6 lg:p-8 space-y-6"
              >
                <header class="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <p class="text-xs font-semibold uppercase tracking-[0.3em] text-gray-400">
                      {{ t("tutorBuilder.step3.label") }}
                    </p>
                    <h2 class="text-3xl font-semibold text-[#1E1E1F] mt-1">
                      {{ t("tutorBuilder.step3.title") }}
                    </h2>
                    <p class="text-sm text-gray-500 mt-2">
                      {{ t("tutorBuilder.step3.description") }}
                    </p>
                  </div>
                  <button
                    type="button"
                    class="px-5 py-2 rounded-full border text-sm font-semibold"
                    :class="createdQuestions.length
                      ? 'border-blue-200 text-blue-600 hover:bg-blue-50'
                      : 'border-gray-200 text-gray-400 cursor-not-allowed'"
                    :disabled="!createdQuestions.length"
                    @click="toggleSelectAll"
                  >
                    {{ allSelected ? t("tutorBuilder.step3.unselectAll") : t("tutorBuilder.step3.selectAll") }}
                  </button>
                </header>

                <div
                  v-if="!createdQuestions.length"
                  class="rounded-3xl border-2 border-dashed border-gray-200 px-6 py-12 text-center text-sm text-gray-500"
                >
                  {{ t("tutorBuilder.step3.empty") }}
                </div>
                <div
                  v-else
                  class="space-y-4"
                >
                  <label
                    v-for="(question, index) in createdQuestions"
                    :key="question.id"
                    class="flex gap-4 p-4 border rounded-3xl hover:border-blue-200 transition cursor-pointer"
                  >
                    <input
                      v-model="selectedQuestionIds"
                      :value="question.id"
                      type="checkbox"
                      class="mt-2 w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    >
                    <div class="flex-1">
                      <div class="flex flex-wrap items-center justify-between gap-2">
                        <p class="text-sm font-semibold text-gray-800">
                          {{ t("tutorBuilder.step3.questionNumber", { number: index + 1 }) }}
                        </p>
                        <span class="text-[10px] uppercase tracking-wide text-gray-400">
                          ID: {{ question.id }}
                        </span>
                      </div>
                      <p class="text-sm text-gray-600 mt-2">
                        {{ question.prompt }}
                      </p>
                      <div class="flex flex-wrap gap-2 mt-3">
                        <span
                          v-for="choice in getChoiceKeys(question)"
                          :key="`preview-${question.id}-${choice}`"
                          class="text-[11px] px-3 py-1 rounded-full border"
                          :class="choice === question.correctChoice
                            ? 'bg-blue-50 border-blue-200 text-blue-600'
                            : 'border-gray-200 text-gray-500'"
                        >
                          {{ choice }}. {{ question.choices[choice] }}
                        </span>
                      </div>
                    </div>
                  </label>
                </div>

                <div class="border-t border-gray-100 pt-6 space-y-4">
                  <div class="flex flex-wrap items-center gap-3 text-sm text-gray-500">
                    <span
                      class="px-3 py-1 rounded-full text-xs"
                      :class="selectionCount ? 'bg-blue-50 text-blue-600' : 'bg-gray-100 text-gray-500'"
                    >
                      {{ t("tutorBuilder.step3.selectionCount", { count: selectionCount }) }}
                    </span>
                    <span
                      v-if="generatedQuizGuid"
                      class="text-xs text-emerald-600"
                    >
                      {{ t("tutorBuilder.step3.generatedGuid") }}
                      <strong>{{ generatedQuizGuid }}</strong>
                    </span>
                    <span
                      v-else-if="selectionCount"
                      class="text-xs"
                    >
                      {{ t("tutorBuilder.step3.ready") }}
                    </span>
                  </div>

                  <form
                    class="space-y-4"
                    @submit.prevent="generateQuiz"
                  >
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label class="text-sm font-medium text-gray-700 mb-2 block">
                          {{ t("tutorBuilder.step3.fields.quizTitleLabel") }}
                        </label>
                        <input
                          v-model.trim="generateForm.title"
                          type="text"
                          :placeholder="t('tutorBuilder.step3.fields.quizTitlePlaceholder')"
                          class="w-full rounded-2xl border border-gray-200 px-4 py-3 text-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                      </div>
                      <div>
                        <label class="text-sm font-medium text-gray-700 mb-2 block">
                          {{ t("tutorBuilder.step3.fields.durationLabel") }}
                        </label>
                        <input
                          v-model.number="generateForm.durationInMinutes"
                          type="number"
                          min="1"
                          :placeholder="t('tutorBuilder.step3.fields.durationPlaceholder')"
                          class="w-full rounded-2xl border border-gray-200 px-4 py-3 text-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                      </div>
                    </div>

                    <div>
                      <label class="text-sm font-medium text-gray-700 mb-2 block">
                        {{ t("tutorBuilder.step3.fields.descriptionLabel") }}
                      </label>
                      <textarea
                        v-model.trim="generateForm.description"
                        rows="3"
                        :placeholder="t('tutorBuilder.step3.fields.descriptionPlaceholder')"
                        class="w-full rounded-2xl border border-gray-200 px-4 py-3 text-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                      />
                    </div>

                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label class="text-sm font-medium text-gray-700 mb-2 block">
                          {{ t("tutorBuilder.step3.fields.pointsLabel") }}
                        </label>
                        <input
                          v-model.number="generateForm.points"
                          type="number"
                          min="0"
                          :placeholder="t('tutorBuilder.step3.fields.pointsPlaceholder')"
                          class="w-full rounded-2xl border border-gray-200 px-4 py-3 text-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                      </div>
                      <div>
                        <label class="text-sm font-medium text-gray-700 mb-2 block">
                          {{ t("tutorBuilder.step3.fields.categoryLabel") }}
                        </label>
                        <select
                          v-model="generateForm.quizCategoryId"
                          class="w-full rounded-2xl border border-gray-200 px-4 py-3 text-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                        >
                          <option
                            v-for="category in categoryOptions"
                            :key="`generate-${category.id}`"
                            :value="category.id"
                          >
                            {{ category.name }}
                          </option>
                        </select>
                      </div>
                    </div>

                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div
                        class="rounded-2xl border border-gray-200 px-4 py-3 flex items-center justify-between"
                      >
                        <div>
                          <p class="text-sm font-semibold text-gray-800">
                            {{ t("tutorBuilder.step3.fields.publicTitle") }}
                          </p>
                          <p class="text-xs text-gray-500">
                            {{ t("tutorBuilder.step3.fields.publicDescription") }}
                          </p>
                        </div>
                        <button
                          type="button"
                          class="w-12 h-6 rounded-full transition relative"
                          :class="generateForm.isPublic ? 'bg-blue-600' : 'bg-gray-200'"
                          :aria-pressed="generateForm.isPublic"
                          @click="generateForm.isPublic = !generateForm.isPublic"
                        >
                          <span
                            class="absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition"
                            :class="generateForm.isPublic ? 'translate-x-6' : ''"
                          />
                        </button>
                      </div>

                      <div
                        class="rounded-2xl border border-gray-200 px-4 py-3 flex items-center justify-between"
                      >
                        <div>
                          <p class="text-sm font-semibold text-gray-800">
                            {{ t("tutorBuilder.step3.fields.reviewTitle") }}
                          </p>
                          <p class="text-xs text-gray-500">
                            {{ t("tutorBuilder.step3.fields.reviewDescription") }}
                          </p>
                        </div>
                        <button
                          type="button"
                          class="w-12 h-6 rounded-full transition relative"
                          :class="generateForm.requireReviewPin ? 'bg-blue-600' : 'bg-gray-200'"
                          :aria-pressed="generateForm.requireReviewPin"
                          @click="generateForm.requireReviewPin = !generateForm.requireReviewPin"
                        >
                          <span
                            class="absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition"
                            :class="generateForm.requireReviewPin ? 'translate-x-6' : ''"
                          />
                        </button>
                      </div>
                    </div>

                    <div v-if="generateForm.requireReviewPin">
                      <label class="text-sm font-medium text-gray-700 mb-2 block">
                        {{ t("tutorBuilder.step3.fields.reviewPinLabel") }}
                      </label>
                      <input
                        v-model="generateForm.reviewPin"
                        type="text"
                        maxlength="4"
                        :placeholder="t('tutorBuilder.step3.fields.reviewPinPlaceholder')"
                        class="w-full rounded-2xl border border-gray-200 px-4 py-3 text-gray-800 tracking-[0.5em] text-center focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                    </div>

                    
                    <div class="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                      <p class="text-xs text-gray-500">
                        {{
                          t("tutorBuilder.step3.fields.subjectGrade", {
                            subject: quizForm.subjectId || "-",
                            grade: quizForm.gradeId || "-",
                          })
                        }}
                      </p>
                      <button
                        type="submit"
                        class="px-8 py-3 rounded-2xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-500 transition disabled:opacity-60 disabled:cursor-not-allowed"
                        :disabled="!canGenerateQuiz"
                      >
                        <span v-if="isGeneratingQuiz">{{ t("tutorBuilder.step1.buttons.submit") }}...</span>
                        <span v-else>{{ t("tutorBuilder.step3.fields.submit") }}</span>
                      </button>
                    </div>
                  </form>

                  <div
                    v-if="generatedQuizMessage"
                    class="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-xs text-emerald-700"
                  >
                    {{ generatedQuizMessage }}
                  </div>
                </div>
              </section>
            </div>
          </main>
        </div>
      </div>
    </ResponsiveView>

    <ResponsiveView mobile-only>
      <div class="min-h-screen bg-[#F7F9FC] flex items-center justify-center px-6 text-center">
        <div class="bg-white rounded-[32px] border border-gray-100 shadow-sm p-8 space-y-4">
          <h2 class="text-2xl font-semibold text-gray-900">
            {{ t("tutorBuilder.mobile.title") }}
          </h2>
          <p class="text-sm text-gray-500">
            {{ t("tutorBuilder.mobile.description") }}
          </p>
          <button
            type="button"
            class="text-blue-600 font-semibold"
            @click="router.back()"
          >
            {{ t("tutorBuilder.mobile.back") }}
          </button>
        </div>
      </div>
    </ResponsiveView>

    <transition name="fade">
      <div
        v-if="showCompletionModal"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      >
        <div class="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl">
          <div class="flex items-start gap-3">
            <div class="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50">
              <i class="fas fa-check text-emerald-600" />
            </div>
            <div class="space-y-1">
              <p class="text-sm font-semibold uppercase tracking-[0.2em] text-emerald-600">
                {{ t("tutorBuilder.modal.status") }}
              </p>
              <h3 class="text-xl font-semibold text-gray-900">
                {{ t("tutorBuilder.modal.title") }}
              </h3>
              <p class="text-sm text-gray-500">
                {{ t("tutorBuilder.modal.description") }}
              </p>
            </div>
          </div>

          <div class="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-end">
            <button
              type="button"
              class="w-full sm:w-auto rounded-2xl border border-gray-200 px-4 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition"
              @click="goToHome"
            >
              {{ t("tutorBuilder.modal.backHome") }}
            </button>
            <button
              type="button"
              class="w-full sm:w-auto rounded-2xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white hover:bg-blue-500 transition"
              @click="startNewQuiz"
            >
              {{ t("tutorBuilder.modal.createAgain") }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import SidebarComponent from "@/components/base/SidebarComponent.vue";
import HeaderComponent from "@/components/base/HeaderComponent.vue";
import ResponsiveView from "@/components/helper/ResponsiveView.vue";
import {
  QuizGradeAll,
  QuizCategories,
  CreateTutorQuiz,
  AddTutorQuizQuestion,
  GenerateTutorQuizFromSelection,
  ArchiveQuizToggle,
} from "@/api/quizApi";
import { QuizListSubject } from "@/api/subjectApi";
import { useAuthStore } from "@/stores/authStore";
import { useSnackbarStore } from "@/stores/snackbarStore";

const router = useRouter();
const { t } = useI18n();
const auth = useAuthStore();
const snackbar = useSnackbarStore();

const rawSubjects = ref([]);
const rawGrades = ref([]);
const rawCategories = ref([]);
const dropdownLoading = ref(false);

const quizForm = reactive({
  title: "",
  subjectId: "",
  gradeId: "",
  description: "",
  durationInMinutes: 25,
  isPublic: true,
  points: 150,
  quizCategoryId: "",
  requireReviewPin: false,
  reviewPin: "",
});
const isCreatingQuiz = ref(false);
const createdQuizGuid = ref("");
const createdQuizResponse = ref(null);

const questionForm = reactive({
  question: "",
  choiceA: "",
  choiceB: "",
  choiceC: "",
  choiceD: "",
  choiceE: "",
  choiceF: "",
  choiceG: "",
  correctChoice: "A",
  isMultipleAnswer: false,
  attachment: null,
});
const attachmentInput = ref(null);
const isSavingQuestion = ref(false);
const createdQuestions = ref([]);
const selectedQuestionIds = ref([]);
const questionListSection = ref(null);
const showCompletionModal = ref(false);

const generateForm = reactive({
  title: "",
  description: "",
  durationInMinutes: 20,
  isPublic: false,
  points: 120,
  quizCategoryId: "",
  requireReviewPin: false,
  reviewPin: "",
});
const isGeneratingQuiz = ref(false);
const generatedQuizResponse = ref(null);

const asArray = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (payload && Array.isArray(payload.data)) return payload.data;
  return [];
};

const pickField = (entity, candidates) => {
  if (!entity) return undefined;
  for (const key of candidates) {
    const value = entity[key];
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return undefined;
};

const subjectOptions = computed(() =>
  rawSubjects.value.map((item, index) => ({
    id:
      pickField(item, ["SubjectId", "subjectId", "Id", "id", "value"]) ?? index,
    name:
      pickField(item, ["SubjectName", "subjectName", "name", "title"]) ??
      `Subject ${index + 1}`,
  }))
);

const gradeOptions = computed(() =>
  rawGrades.value.map((item, index) => ({
    id: pickField(item, ["GradeId", "gradeId", "Id", "id"]) ?? index,
    name:
      pickField(item, ["GradeName", "gradeName", "name", "title"]) ??
      `Grade ${index + 1}`,
  }))
);

const categoryOptions = computed(() =>
  rawCategories.value.map((item, index) => ({
    id:
      pickField(item, ["quizCategoryId", "QuizCategoryId", "Id", "id"]) ??
      index,
    name:
      pickField(item, ["categoryName", "CategoryName", "name", "title"]) ??
      `Category ${index + 1}`,
  }))
);

const questionChoicesPrimary = ["A", "B", "C", "D"];
const questionChoicesExtra = ["E", "F", "G"];
const allChoiceLabels = [...questionChoicesPrimary, ...questionChoicesExtra];
const getChoiceKeys = (question) =>
  allChoiceLabels.filter((label) => Boolean(question?.choices?.[label]));

const userIdAvailable = computed(() => Boolean(auth.userId));
const questionSectionLocked = computed(() => !createdQuizGuid.value);
const selectionCount = computed(() => selectedQuestionIds.value.length);
const allSelected = computed(
  () =>
    createdQuestions.value.length > 0 &&
    selectionCount.value === createdQuestions.value.length
);
const attachmentLabel = computed(() =>
  questionForm.attachment?.name || t("tutorBuilder.step2.noFile")
);
const canGenerateQuiz = computed(
  () =>
    userIdAvailable.value &&
    !!quizForm.subjectId &&
    !!quizForm.gradeId &&
    selectionCount.value > 0 &&
    !!generateForm.title &&
    !isGeneratingQuiz.value
);

const getResponseMessage = (payload) => {
  if (!payload) return "";
  if (typeof payload === "string") return payload;
  if (payload.raw) return payload.raw;
  return (
    pickField(payload, ["message", "Message", "statusMessage"]) ||
    pickField(payload.data || {}, ["message", "Message", "statusMessage"]) ||
    ""
  );
};

const extractGuid = (payload) => {
  if (!payload || typeof payload === "string") return "";
  const buckets = [payload, payload.data];
  for (const bucket of buckets) {
    const guid = pickField(bucket || {}, [
      "quizGuid",
      "QuizGuid",
      "guid",
      "Guid",
      "quizGUID",
    ]);
    if (guid) return String(guid);
  }
  return "";
};

const extractQuestionId = (payload) => {
  if (!payload || typeof payload === "string") return "";
  const buckets = [payload, payload.data];
  for (const bucket of buckets) {
    const id = pickField(bucket || {}, [
      "questionId",
      "QuestionId",
      "id",
      "Id",
      "questionGuid",
    ]);
    if (id !== undefined && id !== null && id !== "") {
      return String(id);
    }
  }
  return "";
};

const createQuizMessage = computed(() => getResponseMessage(createdQuizResponse.value));
const generatedQuizMessage = computed(() =>
  getResponseMessage(generatedQuizResponse.value)
);
const generatedQuizGuid = computed(() => extractGuid(generatedQuizResponse.value));

const fetchDropdowns = async () => {
  dropdownLoading.value = true;
  try {
    const [subjectData, gradeData, categoryData] = await Promise.all([
      QuizListSubject(),
      QuizGradeAll(),
      QuizCategories(),
    ]);
    rawSubjects.value = asArray(subjectData);
    rawGrades.value = asArray(gradeData);
    rawCategories.value = asArray(categoryData);
  } catch (error) {
    console.error("Failed to fetch dropdown data:", error);
    snackbar.trigger(t("tutorBuilder.snackbar.dropdownFailed"), "error");
  } finally {
    dropdownLoading.value = false;
  }
};

const validateQuizShell = () => {
  if (!quizForm.title?.trim()) {
    snackbar.trigger(t("tutorBuilder.snackbar.titleRequired"), "error");
    return false;
  }
  if (!quizForm.subjectId) {
    snackbar.trigger(t("tutorBuilder.snackbar.subjectRequired"), "error");
    return false;
  }
  if (!quizForm.gradeId) {
    snackbar.trigger(t("tutorBuilder.snackbar.gradeRequired"), "error");
    return false;
  }
  if (!quizForm.quizCategoryId) {
    snackbar.trigger(t("tutorBuilder.snackbar.categoryRequired"), "error");
    return false;
  }
  if (quizForm.requireReviewPin && !/^\d{4}$/.test(quizForm.reviewPin || "")) {
    snackbar.trigger(t("tutorBuilder.snackbar.reviewPinInvalid"), "error");
    return false;
  }
  return true;
};

const submitQuizShell = async () => {
  if (!userIdAvailable.value) {
    snackbar.trigger(t("tutorBuilder.snackbar.loginRequired"), "error");
    return;
  }
  if (!validateQuizShell()) return;

  const payload = {
    userId: auth.userId,
    title: quizForm.title.trim(),
    subjectId: quizForm.subjectId,
    gradeId: quizForm.gradeId,
    description: quizForm.description,
    durationInMinutes: Number(quizForm.durationInMinutes) || 0,
    isPublic: Boolean(quizForm.isPublic),
    points: Number(quizForm.points) || 0,
    quizCategoryId: quizForm.quizCategoryId,
    requireReviewPin: Boolean(quizForm.requireReviewPin),
    reviewPin: quizForm.requireReviewPin ? quizForm.reviewPin : "",
  };

  isCreatingQuiz.value = true;
  try {
    const response = await CreateTutorQuiz(payload);
    createdQuizResponse.value = response;
    const guid = extractGuid(response);
    createdQuizGuid.value = guid || "";
    createdQuestions.value = [];
    selectedQuestionIds.value = [];
    generatedQuizResponse.value = null;
    hydrateGenerateForm();

    snackbar.trigger(t("tutorBuilder.snackbar.createSuccess"), "success");
  } catch (error) {
    console.error("CreateTutorQuiz error:", error);
    snackbar.trigger(t("tutorBuilder.snackbar.createFailed"), "error");
  } finally {
    isCreatingQuiz.value = false;
  }
};

const resetBuilder = () => {
  quizForm.title = "";
  quizForm.subjectId = "";
  quizForm.gradeId = "";
  quizForm.description = "";
  quizForm.durationInMinutes = 25;
  quizForm.isPublic = true;
  quizForm.points = 150;
  quizForm.quizCategoryId = categoryOptions.value[0]?.id || "";
  quizForm.requireReviewPin = false;
  quizForm.reviewPin = "";
  createdQuizGuid.value = "";
  createdQuizResponse.value = null;
  resetQuestionForm();
  createdQuestions.value = [];
  selectedQuestionIds.value = [];
  generatedQuizResponse.value = null;
};

const resetQuestionForm = () => {
  questionForm.question = "";
  questionForm.choiceA = "";
  questionForm.choiceB = "";
  questionForm.choiceC = "";
  questionForm.choiceD = "";
  questionForm.choiceE = "";
  questionForm.choiceF = "";
  questionForm.choiceG = "";
  questionForm.correctChoice = "A";
  questionForm.isMultipleAnswer = false;
  questionForm.attachment = null;
  if (attachmentInput.value) attachmentInput.value.value = "";
};

const browseAttachment = () => {
  attachmentInput.value?.click();
};

const onAttachmentChange = (event) => {
  const file = event.target?.files?.[0];
  questionForm.attachment = file || null;
};

const removeAttachment = () => {
  questionForm.attachment = null;
  if (attachmentInput.value) attachmentInput.value.value = "";
};

const scrollToQuestionList = () => {
  questionListSection.value?.scrollIntoView({ behavior: "smooth", block: "start" });
};

const validateQuestionForm = () => {
  if (!questionForm.question?.trim()) {
    snackbar.trigger(t("tutorBuilder.snackbar.questionRequired"), "error");
    return false;
  }
  const requiredField = questionForm[`choice${questionForm.correctChoice}`];
  if (!requiredField?.trim()) {
    snackbar.trigger(t("tutorBuilder.snackbar.correctAnswerRequired"), "error");
    return false;
  }
  return true;
};

const addQuestion = async () => {
  if (questionSectionLocked.value) {
    snackbar.trigger(t("tutorBuilder.snackbar.needQuizFirst"), "error");
    return;
  }
  if (!validateQuestionForm()) return;

  const payload = {
    quizGuid: createdQuizGuid.value,
    userId: auth.userId,
    question: questionForm.question,
    correctChoice: questionForm.correctChoice,
    isMultipleAnswer: questionForm.isMultipleAnswer,
  };
  allChoiceLabels.forEach((choice) => {
    if (questionForm[`choice${choice}`]) {
      payload[`choice${choice}`] = questionForm[`choice${choice}`];
    }
  });
  if (questionForm.attachment) payload.attachment = questionForm.attachment;

  isSavingQuestion.value = true;
  try {
    const response = await AddTutorQuizQuestion(payload);
    const questionId = extractQuestionId(response);
    if (!questionId) {
      snackbar.trigger(t("tutorBuilder.snackbar.missingQuestionId"), "warning");
      return;
    }
    const choices = allChoiceLabels.reduce((acc, label) => {
      const value = questionForm[`choice${label}`];
      if (value?.trim()) acc[label] = value.trim();
      return acc;
    }, {});

    createdQuestions.value = [
      ...createdQuestions.value,
      {
        id: questionId,
        prompt: questionForm.question.trim(),
        choices,
        correctChoice: questionForm.correctChoice,
      },
    ];
    selectedQuestionIds.value = Array.from(
      new Set([...selectedQuestionIds.value, questionId])
    );
    resetQuestionForm();
    snackbar.trigger(t("tutorBuilder.snackbar.addQuestionSuccess"), "success");
    scrollToQuestionList();
  } catch (error) {
    console.error("AddTutorQuizQuestion error:", error);
    snackbar.trigger(t("tutorBuilder.snackbar.addQuestionFailed"), "error");
  } finally {
    isSavingQuestion.value = false;
  }
};

const toggleSelectAll = () => {
  if (!createdQuestions.value.length) return;
  if (allSelected.value) {
    selectedQuestionIds.value = [];
  } else {
    selectedQuestionIds.value = createdQuestions.value.map((q) => q.id);
  }
};

const normalizeQuestionIds = () =>
  selectedQuestionIds.value.map((id) => {
    const numeric = Number(id);
    return Number.isNaN(numeric) ? id : numeric;
  });

const validateGenerateForm = () => {
  if (!selectionCount.value) {
    snackbar.trigger(t("tutorBuilder.snackbar.minSelection"), "error");
    return false;
  }
  if (!generateForm.title?.trim()) {
    snackbar.trigger(t("tutorBuilder.snackbar.generateTitleRequired"), "error");
    return false;
  }
  if (generateForm.requireReviewPin && !/^\d{4}$/.test(generateForm.reviewPin || "")) {
    snackbar.trigger(t("tutorBuilder.snackbar.generateReviewPinInvalid"), "error");
    return false;
  }
  if (!quizForm.subjectId || !quizForm.gradeId) {
    snackbar.trigger(t("tutorBuilder.snackbar.invalidSubjectGrade"), "error");
    return false;
  }
  return true;
};

const archiveSourceQuiz = async () => {
  if (!createdQuizGuid.value) return;
  try {
    await ArchiveQuizToggle({
      guid: createdQuizGuid.value,
      isArchived: true,
      userId: auth.userId,
    });
  } catch (error) {
    console.error("Failed to archive source quiz:", error);
  }
};

const generateQuiz = async () => {
  if (!userIdAvailable.value) {
    snackbar.trigger(t("tutorBuilder.snackbar.loginRequired"), "error");
    return;
  }
  if (!validateGenerateForm()) return;

  const payload = {
    userId: auth.userId,
    subjectId: quizForm.subjectId,
    gradeId: quizForm.gradeId,
    title: generateForm.title.trim(),
    description: generateForm.description,
    durationInMinutes: Number(generateForm.durationInMinutes) || 0,
    isPublic: Boolean(generateForm.isPublic),
    points: Number(generateForm.points) || 0,
    quizCategoryId: generateForm.quizCategoryId || quizForm.quizCategoryId,
    requireReviewPin: Boolean(generateForm.requireReviewPin),
    reviewPin: generateForm.requireReviewPin ? generateForm.reviewPin : "",
    questionIds: normalizeQuestionIds(),
  };

  isGeneratingQuiz.value = true;
  try {
    const response = await GenerateTutorQuizFromSelection(payload);
    generatedQuizResponse.value = response;
    await archiveSourceQuiz();
    snackbar.trigger(t("tutorBuilder.snackbar.generateSuccess"), "success");
    showCompletionModal.value = true;
  } catch (error) {
    console.error("GenerateTutorQuizFromSelection error:", error);
    snackbar.trigger(t("tutorBuilder.snackbar.generateFailed"), "error");
  } finally {
    isGeneratingQuiz.value = false;
  }
};

const hydrateGenerateForm = () => {
  generateForm.title = quizForm.title;
  generateForm.description = quizForm.description;
  generateForm.durationInMinutes = quizForm.durationInMinutes;
  generateForm.points = quizForm.points;
  generateForm.quizCategoryId = quizForm.quizCategoryId;
  generateForm.isPublic = quizForm.isPublic;
  generateForm.requireReviewPin = quizForm.requireReviewPin;
  generateForm.reviewPin = quizForm.requireReviewPin ? quizForm.reviewPin : "";
};

watch(
  () => quizForm.requireReviewPin,
  (next) => {
    if (!next) quizForm.reviewPin = "";
  }
);
watch(
  () => generateForm.requireReviewPin,
  (next) => {
    if (!next) generateForm.reviewPin = "";
  }
);
watch(
  () => createdQuestions.value,
  (questions) => {
    const idSet = new Set(questions.map((q) => q.id));
    selectedQuestionIds.value = selectedQuestionIds.value.filter((id) =>
      idSet.has(id)
    );
  },
  { deep: true }
);
watch(
  () => categoryOptions.value,
  (list) => {
    if (!list.length) return;
    if (!quizForm.quizCategoryId) quizForm.quizCategoryId = list[0].id;
    if (!generateForm.quizCategoryId) generateForm.quizCategoryId = list[0].id;
  },
  { immediate: true }
);

onMounted(fetchDropdowns);

const goToHome = () => {
  showCompletionModal.value = false;
  router.push({ name: "Dashboard" });
};

const startNewQuiz = () => {
  resetBuilder();
  showCompletionModal.value = false;
  if (typeof window !== "undefined") {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
};
</script>

